import { FormEvent, KeyboardEvent, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowUp, Brain, Check, History, MessageSquare, Plus, Sparkles, Square, Target, X
} from 'lucide-react';
import SimpleMarkdown from '../components/agent/SimpleMarkdown';
import { AvaAvatar } from '../components/ava/AvaAvatar';
import { MemorySheet } from '../components/ava/MemorySheet';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Sheet } from '../components/ui/sheet';
import { cn } from '../lib/utils';
import {
  agentApi,
  AgentAction,
  AgentConversationSummary,
  AgentMemory,
  AgentStatus,
  AgentStreamEvent
} from '../lib/agentApi';

type ChatItem =
  | { type: 'message'; role: 'user' | 'assistant'; text: string }
  | { type: 'memory'; kind: string; label: string }
  | { type: 'action'; action: AgentAction };

const TOOL_LABELS: Record<string, string> = {
  save_profile_answer: 'Saving to memory',
  remember: 'Saving a note',
  forget: 'Updating memory',
  set_primary_goal: 'Setting your goal',
  complete_interview: 'Wrapping up your profile',
  get_revenue_summary: 'Checking revenue',
  get_system_analytics: 'Reading the dashboard',
  get_physician_analytics: 'Looking at a clinic',
  get_distributor_analytics: 'Looking at an account manager',
  lookup_invoice: 'Looking up an invoice',
  search_invoices: 'Searching invoices',
  lookup_order: 'Looking up an order',
  search_orders: 'Searching orders',
  get_owed_payouts: 'Checking what is owed',
  list_payouts: 'Checking payouts',
  get_recipient_owed: 'Checking what one person is owed',
  lookup_user: 'Looking up a user',
  search_users: 'Searching users',
  get_user_summary: 'Summarizing a user',
  get_overdue_invoices: 'Checking overdue invoices',
  get_shipped_unpaid_orders: 'Checking shipped, unpaid orders',
  get_shipped_uninvoiced_orders: 'Checking uninvoiced shipments',
  get_order_state_mismatches: 'Checking order status mismatches',
  get_overdue_verifications: 'Checking overdue verifications',
  get_expiring_credentials: 'Checking expiring credentials',
  propose_order_status_update: 'Preparing a status change',
  propose_void_order: 'Preparing a void'
};

const MEMORY_VERBS: Record<string, string> = {
  saved: 'Saved to memory',
  noted: 'Noted',
  forgotten: 'Forgot',
  goal: 'Goal set',
  interview_complete: 'Profile complete'
};

function groupConversations(list: AgentConversationSummary[]) {
  const startOfToday = new Date(); startOfToday.setHours(0, 0, 0, 0);
  const weekAgo = new Date(startOfToday.getTime() - 6 * 86_400_000);
  const groups: { label: string; items: AgentConversationSummary[] }[] = [
    { label: 'Today', items: [] }, { label: 'Previous 7 days', items: [] }, { label: 'Older', items: [] }
  ];
  for (const c of list) {
    const d = new Date(c.updatedAt);
    groups[d >= startOfToday ? 0 : d >= weekAgo ? 1 : 2].items.push(c);
  }
  return groups.filter(g => g.items.length);
}

function TypingDots() {
  return (
    <span className="inline-flex items-center gap-1" aria-label="Ava is typing">
      {[0, 150, 300].map(d => (
        <span key={d} className="h-1.5 w-1.5 animate-bounce rounded-full bg-brand/70" style={{ animationDelay: `${d}ms` }} />
      ))}
    </span>
  );
}

export default function Ava() {
  const [status, setStatus] = useState<AgentStatus | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [conversations, setConversations] = useState<AgentConversationSummary[]>([]);
  const [conversationId, setConversationId] = useState<string | undefined>();
  const [items, setItems] = useState<ChatItem[]>([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [activity, setActivity] = useState<string | null>(null);
  const [chatError, setChatError] = useState<string | null>(null);
  const [memoryOpen, setMemoryOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [interviewDismissed, setInterviewDismissed] = useState(false);
  const [actionBusy, setActionBusy] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const shellRef = useRef<HTMLDivElement>(null);
  const isTouch = useMemo(() => typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches, []);
  const [shellHeight, setShellHeight] = useState<number | undefined>();

  // Fill the viewport below the app header, whatever height the header is.
  // Measured against the visual viewport, which shrinks when a phone's
  // keyboard opens, so the composer stays above the keyboard instead of
  // behind it.
  useLayoutEffect(() => {
    const vv = window.visualViewport;
    const measure = () => {
      const top = (shellRef.current?.getBoundingClientRect().top ?? 0) + window.scrollY;
      const viewport = vv ? vv.height + vv.offsetTop : window.innerHeight;
      setShellHeight(Math.max(320, viewport - top + window.scrollY));
    };
    measure();
    window.addEventListener('resize', measure);
    vv?.addEventListener('resize', measure);
    vv?.addEventListener('scroll', measure);
    return () => {
      window.removeEventListener('resize', measure);
      vv?.removeEventListener('resize', measure);
      vv?.removeEventListener('scroll', measure);
    };
  });

  const memory = status?.memory ?? null;
  const setMemory = (m: AgentMemory) => setStatus(s => (s ? { ...s, memory: m } : s));

  const refreshConversations = useCallback(() => {
    agentApi.conversations().then(setConversations).catch(() => {});
  }, []);
  const refreshMemory = useCallback(() => {
    agentApi.memory().then(m => setStatus(s => (s ? { ...s, memory: m } : s))).catch(() => {});
  }, []);
  useEffect(() => { if (memoryOpen) refreshMemory(); }, [memoryOpen, refreshMemory]);

  useEffect(() => {
    agentApi.status().then(setStatus).catch(e => setLoadError(e.message));
    refreshConversations();
    return () => abortRef.current?.abort();
  }, [refreshConversations]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [items, activity]);

  // Grow the composer with its content, up to a limit.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  }, [input]);

  const goal = useMemo(() => {
    if (!status) return undefined;
    return status.goals.find(g => g.id === memory?.goal?.id) ?? status.goals[0];
  }, [status, memory]);

  const answered = memory?.topics.filter(t => t.answer).length ?? 0;
  const totalTopics = memory?.topics.length ?? 0;
  const needsInterview = memory && memory.interviewStatus !== 'complete' && !interviewDismissed;

  function newConversation() {
    abortRef.current?.abort();
    setConversationId(undefined);
    setItems([]);
    setChatError(null);
    setHistoryOpen(false);
    // On a phone, focusing would throw the keyboard over the starter prompts.
    if (!isTouch) inputRef.current?.focus();
  }

  async function openConversation(id: string) {
    abortRef.current?.abort();
    setChatError(null);
    setHistoryOpen(false);
    try {
      const c = await agentApi.conversation(id);
      setConversationId(c.id);
      setItems([
        ...c.messages.map(m => ({ type: 'message' as const, role: m.role, text: m.text })),
        ...c.actions.map(a => ({ type: 'action' as const, action: a }))
      ]);
    } catch (e: any) {
      setChatError(e.message);
    }
  }

  async function send(body: { message?: string; kickoff?: 'interview' }) {
    if (streaming || !status) return;
    const text = body.message?.trim() || (body.kickoff ? status.kickoffMessage : '');
    if (!text) return;
    setInput('');
    setChatError(null);
    setStreaming(true);
    setActivity('Thinking');
    setItems(prev => [...prev, { type: 'message', role: 'user', text }]);

    const appendText = (t: string) => setItems(prev => {
      const last = prev[prev.length - 1];
      if (last?.type === 'message' && last.role === 'assistant') {
        return [...prev.slice(0, -1), { ...last, text: last.text + t }];
      }
      return [...prev, { type: 'message', role: 'assistant', text: t }];
    });

    const onEvent = (event: AgentStreamEvent) => {
      switch (event.type) {
        case 'conversation': setConversationId(event.conversationId); break;
        case 'text': setActivity(null); appendText(event.text); break;
        case 'tool': setActivity(TOOL_LABELS[event.name] ?? 'Looking that up'); break;
        case 'memory':
          setItems(prev => [...prev, { type: 'memory', kind: event.event.kind, label: event.event.label }]);
          refreshMemory();
          break;
        case 'pending_action':
          setItems(prev => [...prev, { type: 'action', action: { ...event.action, status: 'pending' } }]);
          break;
        case 'error': setChatError(event.message); break;
      }
    };

    const controller = new AbortController();
    abortRef.current = controller;
    try {
      await agentApi.chat(
        { conversationId, ...(body.message ? { message: body.message } : { kickoff: body.kickoff }) },
        onEvent,
        controller.signal
      );
    } catch (e: any) {
      if (e.name !== 'AbortError') setChatError(e.message);
    } finally {
      setStreaming(false);
      setActivity(null);
      refreshConversations();
    }
  }

  function stop() {
    abortRef.current?.abort();
  }

  async function resolveAction(action: AgentAction, confirm: boolean) {
    setActionBusy(action.id);
    const update = (patch: Partial<AgentAction>) =>
      setItems(prev => prev.map(i => (i.type === 'action' && i.action.id === action.id ? { ...i, action: { ...i.action, ...patch } } : i)));
    try {
      const result = confirm ? await agentApi.confirmAction(action.id) : await agentApi.rejectAction(action.id);
      update({ status: result.status, result: (result as any).result });
    } catch (e: any) {
      update({ status: confirm ? 'failed' : action.status, result: e.message });
    } finally {
      setActionBusy(null);
    }
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    send({ message: input });
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    // On touch keyboards Return is the only way to start a new line, so it
    // inserts one there; sending is the arrow button.
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing && !isTouch) {
      e.preventDefault();
      send({ message: input });
    }
  }

  if (loadError) {
    return (
      <div className="mx-auto mt-16 max-w-md text-center">
        <AvaAvatar size="lg" className="mx-auto" />
        <p className="mt-4 text-sm text-gray-600 dark:text-gray-300">{loadError}</p>
      </div>
    );
  }
  if (!status) {
    return <div className="flex justify-center p-16"><AvaAvatar size="lg" pulse /></div>;
  }

  const history = (
    <div className="flex h-full flex-col">
      <Button onClick={newConversation} className="w-full justify-start"><Plus className="h-4 w-4" /> New chat</Button>
      <nav className="mt-4 flex-1 space-y-5 overflow-y-auto">
        {groupConversations(conversations).map(g => (
          <div key={g.label}>
            <div className="px-2 pb-1 text-[11px] font-medium uppercase tracking-wider text-gray-400">{g.label}</div>
            {g.items.map(c => (
              <button
                key={c.id}
                onClick={() => openConversation(c.id)}
                className={cn(
                  'flex w-full items-center gap-2 rounded-lg px-2 py-2.5 text-left text-sm transition-colors lg:py-1.5',
                  c.id === conversationId
                    ? 'bg-brand-muted text-brand-dark dark:bg-brand/15 dark:text-brand-light'
                    : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-neutral-800'
                )}
              >
                <MessageSquare className="h-3.5 w-3.5 shrink-0 opacity-60" />
                <span className="truncate">{c.title || 'New chat'}</span>
              </button>
            ))}
          </div>
        ))}
      </nav>
      <button
        onClick={() => setMemoryOpen(true)}
        className="mt-4 flex items-center gap-3 rounded-xl border border-gray-200 p-3 text-left transition-colors hover:border-brand/40 hover:bg-brand-muted/40 dark:border-neutral-700 dark:hover:bg-brand/10"
      >
        <Brain className="h-4 w-4 text-brand" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-gray-900 dark:text-white">What Ava knows</div>
          <div className="mt-1 h-1 overflow-hidden rounded-full bg-gray-100 dark:bg-neutral-800">
            <div className="h-full rounded-full bg-gradient-to-r from-brand to-accent" style={{ width: `${totalTopics ? (answered / totalTopics) * 100 : 0}%` }} />
          </div>
        </div>
        <span className="text-xs text-gray-500">{answered}/{totalTopics}</span>
      </button>
    </div>
  );

  const lastItem = items[items.length - 1];
  const waitingForText = streaming && !(lastItem?.type === 'message' && lastItem.role === 'assistant' && lastItem.text);

  return (
    <div ref={shellRef} style={{ height: shellHeight }} className="-mx-4 -mt-4 -mb-24 flex lg:-mx-8 lg:-mt-8 lg:-mb-28">
      {/* History rail */}
      <aside className="hidden w-64 shrink-0 border-r border-gray-100 bg-gray-50/60 p-4 lg:block dark:border-neutral-800 dark:bg-neutral-900/40">
        {history}
      </aside>

      <section className="relative flex min-w-0 flex-1 flex-col bg-white dark:bg-neutral-950">
        {/* Header */}
        <header className="flex items-center justify-between gap-2 border-b border-gray-100 px-2 py-2 sm:gap-3 sm:px-4 sm:py-3 lg:px-6 dark:border-neutral-800">
          <div className="flex min-w-0 items-center gap-2 sm:gap-3">
            <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setHistoryOpen(true)} aria-label="Chat history">
              <History className="h-4 w-4" />
            </Button>
            <AvaAvatar size="md" pulse={streaming} />
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h1 className="text-base font-semibold text-gray-900 dark:text-white">{status.assistant.name}</h1>
                <span className={cn('h-1.5 w-1.5 rounded-full', status.available ? 'bg-emerald-500' : 'bg-gray-300')} />
              </div>
              <p className="truncate text-xs text-gray-500 dark:text-gray-400">{status.assistant.tagline}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {memory?.goal && (
              <Badge variant="gold" className="hidden sm:inline-flex"><Target className="h-3 w-3" /> {memory.goal.label}</Badge>
            )}
            <Button variant="outline" size="sm" onClick={() => setMemoryOpen(true)} aria-label="Memory">
              <Brain className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Memory</span>
            </Button>
          </div>
        </header>

        {/* Conversation */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto">
          <div className="mx-auto max-w-3xl px-3 py-5 sm:px-4 sm:py-8 lg:px-6">
            {items.length === 0 ? (
              <div className="animate-fade-in">
                <div className="flex flex-col items-center text-center">
                  <AvaAvatar size="xl" />
                  <h2 className="mt-5 text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
                    Hi {status.user.firstName}, I'm Ava.
                  </h2>
                  <p className="mt-2 max-w-md text-sm text-gray-500 dark:text-gray-400">
                    I'm here to help you get the most out of Bloom. Ask me anything, or let me get to know your business first.
                  </p>
                </div>

                {needsInterview && (
                  <div className="relative mt-8 overflow-hidden rounded-2xl border border-brand/20 bg-gradient-to-br from-brand-muted via-white to-accent-light/30 p-5 dark:border-brand/30 dark:from-brand/15 dark:via-neutral-900 dark:to-accent/10">
                    <button onClick={() => setInterviewDismissed(true)} className="absolute right-3 top-3 rounded-md p-1 text-gray-400 hover:text-gray-600" aria-label="Not now">
                      <X className="h-4 w-4" />
                    </button>
                    <div className="flex items-start gap-4">
                      <div className="rounded-xl bg-white/80 p-2 shadow-sm dark:bg-neutral-800"><Sparkles className="h-5 w-5 text-brand" /></div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 dark:text-white">
                          {memory!.interviewStatus === 'in_progress' ? 'Finish getting set up' : 'Let’s get to know your business'}
                        </h3>
                        <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                          A few quick questions so I can tailor Bloom to how you work. About 5 minutes, and you can skip anything.
                        </p>
                        <Button className="mt-4" onClick={() => send({ kickoff: 'interview' })} disabled={!status.available || streaming}>
                          {memory!.interviewStatus === 'in_progress' ? 'Pick up where we left off' : 'Let’s start'}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {goal && (
                  <div className="-mx-3 mt-6 flex snap-x snap-mandatory scroll-px-3 gap-3 overflow-x-auto px-3 pb-1 sm:mx-0 sm:mt-8 sm:grid sm:grid-cols-2 sm:overflow-visible sm:px-0 sm:pb-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                    {goal.starterPrompts.map(p => (
                      <button
                        key={p}
                        onClick={() => send({ message: p })}
                        disabled={!status.available || streaming}
                        className="w-[75%] shrink-0 snap-start rounded-xl border border-gray-200 p-4 text-left text-sm text-gray-700 transition-all hover:-translate-y-0.5 hover:border-brand/40 hover:shadow-brand disabled:opacity-50 sm:w-auto dark:border-neutral-800 dark:text-gray-200"
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-6">
                {items.map((item, i) => {
                  if (item.type === 'message' && item.role === 'user') {
                    return (
                      <div key={i} className="flex justify-end animate-fade-in">
                        <div className="max-w-[88%] whitespace-pre-wrap break-words rounded-2xl sm:max-w-[80%] rounded-br-md bg-brand px-4 py-2.5 text-sm text-white shadow-sm">
                          {item.text}
                        </div>
                      </div>
                    );
                  }
                  if (item.type === 'message') {
                    return (
                      <div key={i} className="flex gap-2 animate-fade-in sm:gap-3">
                        <AvaAvatar size="sm" className="mt-0.5" />
                        <div className="min-w-0 flex-1 break-words text-gray-800 dark:text-gray-100 [&_pre]:overflow-x-auto [&_table]:block [&_table]:max-w-full [&_table]:overflow-x-auto">
                          <SimpleMarkdown text={item.text} />
                        </div>
                      </div>
                    );
                  }
                  if (item.type === 'memory') {
                    return (
                      <div key={i} className="flex justify-center animate-fade-in">
                        <button
                          onClick={() => setMemoryOpen(true)}
                          className="inline-flex items-center gap-1.5 rounded-full border border-brand/20 bg-brand-muted/60 px-3 py-1 text-xs text-brand-dark transition-colors hover:bg-brand-muted dark:border-brand/30 dark:bg-brand/10 dark:text-brand-light"
                        >
                          <Check className="h-3 w-3" />
                          {MEMORY_VERBS[item.kind] ?? 'Memory updated'}{item.kind === 'interview_complete' ? '' : ` · ${item.label}`}
                        </button>
                      </div>
                    );
                  }
                  const a = item.action;
                  return (
                    <div key={i} className="rounded-2xl sm:ml-10 border border-accent/40 bg-accent-light/20 p-4 animate-fade-in dark:border-accent/30 dark:bg-accent/10">
                      <div className="text-[11px] font-semibold uppercase tracking-wider text-accent-dark dark:text-accent-light">Proposed change</div>
                      <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{a.summary}</p>
                      {a.status === 'pending' ? (
                        <div className="mt-3 flex gap-2">
                          <Button size="sm" variant="success" disabled={actionBusy === a.id} onClick={() => resolveAction(a, true)}><Check className="h-3.5 w-3.5" /> Confirm</Button>
                          <Button size="sm" variant="outline" disabled={actionBusy === a.id} onClick={() => resolveAction(a, false)}>Reject</Button>
                        </div>
                      ) : (
                        <p className={cn('mt-2 text-sm', a.status === 'confirmed' ? 'text-emerald-700 dark:text-emerald-400' : 'text-gray-600 dark:text-gray-400')}>
                          {a.status === 'confirmed' ? 'Done' : a.status === 'failed' ? 'Failed' : a.status === 'rejected' ? 'Rejected' : 'Expired'}
                          {a.result ? ` · ${a.result}` : ''}
                        </p>
                      )}
                    </div>
                  );
                })}

                {waitingForText && (
                  <div className="flex items-center gap-3 animate-fade-in">
                    <AvaAvatar size="sm" pulse />
                    <span className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                      <TypingDots />
                      {activity && activity !== 'Thinking' && <span>{activity}…</span>}
                    </span>
                  </div>
                )}
                {chatError && <p className="text-center text-sm text-red-600">{chatError}</p>}
              </div>
            )}
          </div>
        </div>

        {/* Composer */}
        <div className="px-2 pb-2 sm:px-4 sm:pb-4 lg:px-6">
          <form
            onSubmit={onSubmit}
            className="mx-auto flex max-w-3xl items-end gap-2 rounded-2xl border border-gray-200 bg-white p-2 shadow-lg shadow-gray-200/50 focus-within:border-brand/40 dark:border-neutral-700 dark:bg-neutral-900 dark:shadow-none"
          >
            <textarea
              ref={inputRef}
              rows={1}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={status.available ? 'Message Ava…' : 'Ava isn’t configured on this environment yet'}
              disabled={!status.available}
              className="max-h-[200px] flex-1 resize-none bg-transparent px-2 py-2 text-base sm:text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none dark:text-gray-100"
            />
            {streaming ? (
              <Button size="icon" variant="secondary" onClick={stop} aria-label="Stop" className="rounded-xl">
                <Square className="h-3.5 w-3.5 fill-current" />
              </Button>
            ) : (
              <Button type="submit" size="icon" disabled={!input.trim() || !status.available} aria-label="Send" className="rounded-xl bg-gradient-to-br from-brand to-brand-dark">
                <ArrowUp className="h-4 w-4" />
              </Button>
            )}
          </form>
          <p className="mx-auto mt-2 hidden max-w-3xl text-center text-[11px] text-gray-400 sm:block">
            Ava can make mistakes. Changes only happen when you confirm them.
          </p>
        </div>
      </section>

      <MemorySheet
        open={memoryOpen}
        onClose={() => setMemoryOpen(false)}
        memory={memory}
        onChange={setMemory}
        onAsk={label => { setMemoryOpen(false); send({ message: `Ask me about ${label.toLowerCase()}.` }); }}
      />
      <Sheet open={historyOpen} onClose={() => setHistoryOpen(false)} title="Chats">
        {history}
      </Sheet>
    </div>
  );
}
